<template>
  <div>
    <b-row class="match-height">
      <b-col v-for="tile in tiles" :key="tile.label" cols="6" md="3" class="mb-1">
        <b-card :border-variant="tile.variant" class="border mb-0 text-center">
          <h2 :class="'mb-0 text-' + tile.variant">{{ tile.value }}</h2>
          <small class="text-muted">{{ tile.label }}</small>
        </b-card>
      </b-col>
    </b-row>

    <b-row class="mb-1">
      <b-col cols="12" md="6" class="mb-1">
        <b-card class="border mb-0 h-100">
          <h6 class="mb-50">
            Form sections signed
            <b-badge :variant="sectionsSigned === sectionsTotal && sectionsTotal ? 'success' : 'warning'" class="ml-25">
              {{ sectionsSigned }} / {{ sectionsTotal }}
            </b-badge>
          </h6>
          <template v-if="pendingSectionTitles.length">
            <small class="text-muted d-block">Waiting for signature:</small>
            <b-badge v-for="title in pendingSectionTitles" :key="title" variant="light-warning" class="mr-25 mb-25">
              {{ title }}
            </b-badge>
          </template>
          <small v-else-if="sectionsTotal" class="text-success">All sections have been signed.</small>
          <small v-else class="text-muted">No form sections apply yet.</small>
        </b-card>
      </b-col>
      <b-col cols="12" md="6" class="mb-1">
        <b-card class="border mb-0 h-100">
          <h6 class="mb-50">
            Open requests from the client
            <b-badge :variant="openRequests ? 'warning' : 'light-secondary'" class="ml-25">{{ openRequests }}</b-badge>
          </h6>
          <small class="text-muted">
            Files the client asked SFC to provide. They are handled on the Documents tab.
          </small>
        </b-card>
      </b-col>
    </b-row>

    <div class="d-flex justify-content-between align-items-center mb-50">
      <h5 class="mb-0">Outstanding documents</h5>
      <b-button
        variant="primary"
        size="sm"
        :disabled="sending || !outstandingRows.length"
        @click="sendReminder"
      >
        <b-spinner v-if="sending" small class="mr-25" />
        <feather-icon v-else icon="SendIcon" size="14" class="mr-25" />
        Send reminder
      </b-button>
    </div>
    <b-table
      :items="outstandingRows"
      :fields="outstandingFields"
      show-empty
      empty-text="Nothing is outstanding — every requested document has been submitted or approved."
      responsive
      small
    >
      <template #cell(Name)="{ item }">
        {{ item.Name }}
        <small v-if="item.Note" class="text-muted d-block">{{ item.Note }}</small>
      </template>
      <template #cell(Person)="{ item }">
        <span v-if="item.Scope !== 'Account'">{{ item.PersonLabel || item.PersonName || item.Scope }}</span>
        <span v-else class="text-muted">Account</span>
      </template>
      <template #cell(Status)="{ item }">
        <b-badge :variant="requirementStatusVariant(item.Status)">{{ requirementStatusLabel(item.Status) }}</b-badge>
        <small v-if="item.RejectReason" class="text-danger d-block">{{ item.RejectReason }}</small>
      </template>
      <template #cell(NotifiedAt)="{ value }">
        {{ formatDateTime(value) }}
      </template>
    </b-table>
  </div>
</template>

<script>
import onboardingMixin from "./onboardingMixin";

export default {
  name: "OnboardingSummaryTab",

  mixins: [onboardingMixin],

  props: {
    onboardingId: { type: Number, required: true },
    summary: { type: Object, default: () => ({}) },
    requirements: { type: Array, default: () => [] },
    formSections: { type: Array, default: () => [] },
    sectionCatalogue: { type: Array, default: () => [] },
  },

  data() {
    return {
      sending: false,
      outstandingFields: [
        { key: "Name", label: "Document" },
        { key: "Person", label: "Required from" },
        { key: "Status", label: "Status" },
        { key: "NotifiedAt", label: "Last notified" },
      ],
    };
  },

  computed: {
    tiles() {
      const s = this.summary || {};
      return [
        { label: "Outstanding", value: s.outstanding || 0, variant: "warning" },
        { label: "Submitted", value: s.submitted || 0, variant: "info" },
        { label: "Approved", value: s.approved || 0, variant: "success" },
        { label: "Rejected", value: s.rejected || 0, variant: "danger" },
      ];
    },

    sectionsSigned() {
      return (this.summary && this.summary.sectionsSigned) || 0;
    },

    sectionsTotal() {
      return (this.summary && this.summary.sectionsTotal) || 0;
    },

    openRequests() {
      return (this.summary && this.summary.openRequests) || 0;
    },

    pendingSectionTitles() {
      const pending = (this.summary && this.summary.sectionsPending) || [];
      return pending.map((key) => {
        const section =
          this.formSections.find((s) => s.key === key) ||
          this.sectionCatalogue.find((s) => s.key === key);
        return section ? section.title : key;
      });
    },

    outstandingRows() {
      return this.requirements.filter((r) => r.Status === "Requested" || r.Status === "Rejected");
    },
  },

  methods: {
    sendReminder() {
      const count = this.outstandingRows.length;
      this.confirmBox(
        `Email the active contacts a reminder listing the ${count} outstanding document${count === 1 ? "" : "s"}?`,
        { title: "Send Reminder", okVariant: "primary", okTitle: "Send" }
      ).then((confirmed) => {
        if (!confirmed) return;
        this.sending = true;
        this.dispatchOnboarding("sendReminder", { onboardingId: this.onboardingId })
          .then((res) => {
            this.toastSuccess((res.data && res.data.message) || "Reminder sent.", "Reminder Sent");
            this.$emit("refresh");
          })
          .catch((err) => this.toastError(err, "Failed to send the reminder."))
          .finally(() => {
            this.sending = false;
          });
      });
    },
  },
};
</script>
